
import React, { useState } from "react";
import { Guard } from "../types";
import { format } from "date-fns";
import { ContactButtons } from "@/components/common/ContactButtons";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { updateGuard } from "../services/guardService";
import { useToast } from "@/hooks/use-toast";
import { useUserRoles } from "@/hooks/useUserRoles";
import { UserRoleSelect } from "@/pages/users/components/UserRoleSelect";
import { UserRole } from "@/pages/users/types";
import { useQueryClient } from "@tanstack/react-query";

interface GuardInfoProps {
  guard: Guard;
  onUpdate?: (updatedGuard: Guard) => void;
}

export const GuardInfo = ({ guard, onUpdate }: GuardInfoProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Guard>>({
    first_name: guard.first_name,
    last_name: guard.last_name,
    code: guard.code,
    mobile_phone: guard.mobile_phone || "",
    email: guard.email || "",
    afm: guard.afm || "",
    date_of_birth: guard.date_of_birth || "",
    emergency_contact: guard.emergency_contact || "",
    license_expiry_date: guard.license_expiry_date || "",
    hiring_date: guard.hiring_date || "",
    trietia: guard.trietia || 0,
    Role: guard.Role || "guard",
    gender: guard.gender || ""
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [emailUpdateError, setEmailUpdateError] = useState<string | null>(null);
  const { isAdmin } = useUserRoles();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), 'yyyy-MM-dd');
    } catch {
      return "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Reset email error if user edits the email field
    if (name === 'email') {
      setEmailUpdateError(null);
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (role: UserRole) => {
    setFormData(prev => ({ ...prev, Role: role }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setEmailUpdateError(null);
      
      // Save the updated guard information
      const updatedGuardData = await updateGuard(guard.id, formData);
      
      toast({
        title: "Success",
        description: "Guard information updated successfully",
      });
      
      // Invalidate relevant queries to refresh data across the app
      queryClient.invalidateQueries({ queryKey: ["user-credentials"] });
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
      queryClient.invalidateQueries({ queryKey: ["guards"] });
      
      // If there's an onUpdate callback, call it with the updated guard data
      if (onUpdate && updatedGuardData?.[0]) {
        onUpdate(updatedGuardData[0] as Guard);
      }
      
      setIsEditing(false);
    } catch (error: any) {
      // Check if it's an email-specific error
      if (error.message?.toLowerCase().includes('email')) {
        setEmailUpdateError('Failed to update email in authentication system. The profile was updated but sign-in email remains unchanged.');
        toast({
          title: "Partial Update",
          description: "Profile updated but email change failed in authentication system",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: `Failed to update guard: ${error.message}`,
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: guard.first_name,
      last_name: guard.last_name,
      code: guard.code,
      mobile_phone: guard.mobile_phone || "",
      email: guard.email || "",
      afm: guard.afm || "",
      date_of_birth: guard.date_of_birth || "",
      emergency_contact: guard.emergency_contact || "",
      license_expiry_date: guard.license_expiry_date || "",
      hiring_date: guard.hiring_date || "",
      trietia: guard.trietia || 0,
      Role: guard.Role || "guard",
      gender: guard.gender || ""
    });
    setIsEditing(false);
    setEmailUpdateError(null);
  };

  // If not admin, don't show edit button or option to edit
  if (!isAdmin) {
    const displayRole = (guard.user_roles?.[0]?.role || guard.Role) === 'guard' ? 'user' : (guard.user_roles?.[0]?.role || guard.Role || 'user');
    
    return (
      <div className="space-y-4 w-full">
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">
              {guard.first_name} {guard.last_name}
            </h3>
            <Badge variant="secondary" className="uppercase">
              {displayRole}
            </Badge>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div className="space-y-2 px-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Code:</span>
              <span className="text-sm">{guard.code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">ΑΦΜ:</span>
              <span className="text-sm">{guard.afm || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Phone:</span>
              <span className="text-sm">{guard.mobile_phone || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Email:</span>
              <span className="text-sm">{guard.email || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Emergency:</span>
              <span className="text-sm">{guard.emergency_contact || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Τριετία:</span>
              <span className="text-sm">{guard.trietia || "-"}</span>
            </div>
            {guard.gender && (
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">Gender:</span>
                <span className="text-sm capitalize">{guard.gender}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-2 px-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Date of Birth:</span>
              <span className="text-sm">
                {guard.date_of_birth ? format(new Date(guard.date_of_birth), 'dd/MM/yyyy') : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Hiring Date:</span>
              <span className="text-sm">
                {guard.hiring_date ? format(new Date(guard.hiring_date), 'dd/MM/yyyy') : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">License Expiry:</span>
              <span className="text-sm">
                {guard.license_expiry_date ? format(new Date(guard.license_expiry_date), 'dd/MM/yyyy') : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Status:</span>
              <span className="text-sm">{guard.status || "active"}</span>
            </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">Role:</span>
            <span className="text-sm">{(guard.user_roles?.[0]?.role || guard.Role) === 'guard' ? 'user' : (guard.user_roles?.[0]?.role || guard.Role || "N/A")}</span>
          </div>
          </div>
        </div>
        
        <Separator className="my-4" />
        
        <div className="flex flex-col items-center justify-center w-full gap-4 mt-2">
          {guard.mobile_phone && (
            <Button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `tel:${guard.mobile_phone}`;
              }}
              className="bg-green-500 hover:bg-green-600 text-white flex items-center gap-2 w-full sm:w-auto"
            >
              <Phone className="h-4 w-4" />
              <span>Call Guard</span>
            </Button>
          )}
          
          <div className="flex items-center justify-center w-full gap-4">
            <ContactButtons 
              phoneNumber={guard.mobile_phone} 
              email={guard.email}
              name={`${guard.first_name} ${guard.last_name}`}
            />
          </div>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-4 w-full">
        <div className="flex justify-between mb-4">
          <h3 className="text-lg font-semibold">Edit Guard Information</h3>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              size="sm" 
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_name">First Name</Label>
            <Input 
              id="first_name" 
              name="first_name" 
              value={formData.first_name} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="last_name">Last Name</Label>
            <Input 
              id="last_name" 
              name="last_name" 
              value={formData.last_name} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="code">Code</Label>
            <Input 
              id="code" 
              name="code" 
              value={formData.code} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="afm">ΑΦΜ</Label>
            <Input 
              id="afm" 
              name="afm" 
              value={formData.afm} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="mobile_phone">Mobile Phone</Label>
            <Input 
              id="mobile_phone" 
              name="mobile_phone" 
              value={formData.mobile_phone} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="emergency_contact">Emergency Contact</Label>
            <Input 
              id="emergency_contact" 
              name="emergency_contact" 
              value={formData.emergency_contact} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email" 
              name="email" 
              type="email" 
              value={formData.email} 
              onChange={handleChange} 
              className={emailUpdateError ? "border-destructive" : ""}
            />
            {emailUpdateError && (
              <p className="text-destructive text-xs mt-1">{emailUpdateError}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="trietia">Τριετία (0-3)</Label>
            <Input 
              id="trietia" 
              name="trietia" 
              type="number"
              min="0"
              max="3"
              value={formData.trietia?.toString() || "0"} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="date_of_birth">Date of Birth</Label>
            <Input 
              id="date_of_birth" 
              name="date_of_birth" 
              type="date" 
              value={formatDate(formData.date_of_birth as string)} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="hiring_date">Hiring Date</Label>
            <Input 
              id="hiring_date" 
              name="hiring_date" 
              type="date" 
              value={formatDate(formData.hiring_date as string)} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="license_expiry_date">License Expiry Date</Label>
            <Input 
              id="license_expiry_date" 
              name="license_expiry_date" 
              type="date" 
              value={formatDate(formData.license_expiry_date as string)} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <UserRoleSelect 
              defaultValue={formData.Role as UserRole || 'guard'} 
              onValueChange={handleRoleChange} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="gender">Gender</Label>
            <select 
              id="gender" 
              name="gender" 
              value={formData.gender || ""} 
              onChange={handleSelectChange}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>
    );
  }

  const displayRole = (guard.user_roles?.[0]?.role || guard.Role) === 'guard' ? 'user' : (guard.user_roles?.[0]?.role || guard.Role || 'user');
  
  return (
    <div className="space-y-4 w-full">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">
            {guard.first_name} {guard.last_name}
          </h3>
          <Badge variant="secondary" className="uppercase">
            {displayRole}
          </Badge>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div className="space-y-2 px-2">
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">Code:</span>
            <span className="text-sm">{guard.code}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">ΑΦΜ:</span>
            <span className="text-sm">{guard.afm || "-"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">Phone:</span>
            <span className="text-sm">{guard.mobile_phone || "-"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">Email:</span>
            <span className="text-sm">{guard.email || "-"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">Emergency:</span>
            <span className="text-sm">{guard.emergency_contact || "-"}</span>
          </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Τριετία:</span>
              <span className="text-sm">{guard.trietia || "-"}</span>
            </div>
            {guard.gender && (
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">Gender:</span>
                <span className="text-sm capitalize">{guard.gender}</span>
              </div>
            )}
          </div>
        
        <div className="space-y-2 px-2">
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">Date of Birth:</span>
            <span className="text-sm">
              {guard.date_of_birth ? format(new Date(guard.date_of_birth), 'dd/MM/yyyy') : "-"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">Hiring Date:</span>
            <span className="text-sm">
              {guard.hiring_date ? format(new Date(guard.hiring_date), 'dd/MM/yyyy') : "-"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">License Expiry:</span>
            <span className="text-sm">
              {guard.license_expiry_date ? format(new Date(guard.license_expiry_date), 'dd/MM/yyyy') : "-"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">Status:</span>
            <span className="text-sm">{guard.status || "active"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-600">Role:</span>
            <span className="text-sm">{(guard.user_roles?.[0]?.role || guard.Role) === 'guard' ? 'user' : (guard.user_roles?.[0]?.role || guard.Role || "N/A")}</span>
          </div>
        </div>
      </div>
      
      <Separator className="my-4" />
      
      <div className="flex flex-col items-center justify-center w-full gap-4 mt-2">
        {guard.mobile_phone && (
          <Button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `tel:${guard.mobile_phone}`;
            }}
            className="bg-green-500 hover:bg-green-600 text-white flex items-center gap-2 w-full sm:w-auto"
          >
            <Phone className="h-4 w-4" />
            <span>Call Guard</span>
          </Button>
        )}
        
        <div className="flex items-center justify-center w-full gap-4">
          <ContactButtons 
            phoneNumber={guard.mobile_phone} 
            email={guard.email}
            name={`${guard.first_name} ${guard.last_name}`}
          />
        </div>
      </div>
    </div>
  );
};
